import type { BotaoTemplate } from './template-text.js';

/**
 * Os textos que precisam de aprovação da Meta, escritos dentro das regras dela.
 *
 * São a etapa 1 e a etapa 8 da jornada (ver JORNADA-DO-PARTICIPANTE.md). As
 * etapas 2 a 7 NÃO estão aqui de propósito: elas acontecem com a janela de 24
 * horas aberta, e ali a IA escreve livremente. Só quem inicia conversa — ou fala
 * depois de 24 horas de silêncio — precisa passar pela análise.
 *
 * Vêm com o produto, em código. O catálogo que existia antes era conteúdo de
 * banco que alguém da plataforma precisava cadastrar pelo painel Master — e
 * enquanto ninguém cadastrasse, a tela do cliente era um beco.
 *
 * Cada texto obedece ao que `validarTemplate` cobra: não começa nem termina com
 * variável, não repete variável, não cola duas variáveis, o rodapé não tem
 * nenhuma e o corpo cabe em 1024 caracteres. Os textos originais da
 * especificação violavam três dessas — está registrado no documento, com o
 * antes e o depois.
 */
export interface ModeloPronto {
  id: string;
  nome: string;
  /** Agrupador nosso, o mesmo do campo "Tipo" do editor. */
  categoria: string;
  /** Quando esta mensagem sai. É o que decide qual modelo a pessoa escolhe. */
  quando: string;
  body: string;
  footer?: string;
  botoes?: BotaoTemplate[];
}

/**
 * Os dois rótulos do primeiro passo, escritos UMA vez.
 *
 * Três modelos perguntam a mesma coisa — as boas-vindas, o lembrete de quem não
 * respondeu e a reabertura — e as três precisam usar as mesmas palavras: quem
 * recebe o lembrete já viu o convite, e duas chamadas diferentes para a mesma
 * ação leem como duas ações.
 *
 * O rótulo de recusa também é o que `opt-out.ts` reconhece. Editá-lo aqui sem
 * editar lá quebraria a saída em silêncio — e é por isso que
 * `opt-out.spec.ts` lê justamente destas constantes.
 */
/**
 * O aviso de saída, e por que ele mora SÓ nas boas-vindas.
 *
 * É a primeira mensagem — a única em que a pessoa ainda não sabe o que é isto
 * nem como fazer parar. Repeti-lo no rodapé de toda mensagem seguinte não
 * acrescenta informação: quem chegou na segunda já viu a primeira, e os modelos
 * da jornada têm o botão "Não quero participar" ao lado, que resolve em um toque
 * em vez de pedir que alguém digite uma palavra.
 */
const SAIDA = 'Responda SAIR para não receber mais mensagens.';

const ACEITAR_JORNADA: BotaoTemplate = { tipo: 'RESPOSTA_RAPIDA', texto: 'Quero participar' };
const RECUSAR_JORNADA: BotaoTemplate = { tipo: 'RESPOSTA_RAPIDA', texto: 'Não quero participar' };

export const MODELOS_PRONTOS: ModeloPronto[] = [
  {
    id: 'boas_vindas',
    nome: '1. Boas-vindas',
    categoria: 'convite_jornada',
    quando: 'A primeira mensagem, quando a organização inicia a jornada. Apresenta a experiência.',
    /**
     * O texto da especificação nomeava o evento duas vezes — "boas-vindas ao
     * {{nome_evento}}" e "a organização do {{nome_evento}} nos convidou". A Meta
     * recusa variável repetida, então a segunda virou texto fixo: a frase
     * anterior já nomeou o evento, e o sentido não muda.
     */
    body:
      'Olá, {{nome_participante}}! 👋 Boas-vindas à experiência de conexões do ' +
      '{{nome_evento}}!\n\n' +
      'Sou a MatériaPrimAi, a inteligência artificial que vai ajudar você a encontrar ' +
      'pessoas com quem vale a pena conversar durante o evento. 🤝\n\n' +
      'A organização do evento nos convidou para tornar seus encontros mais relevantes. ' +
      'A partir dos seus interesses e objetivos, vamos sugerir conexões que podem abrir ' +
      'portas para parcerias, troca de experiências e novas oportunidades.\n\n' +
      'Para isso, vamos conhecer um pouco sobre você em uma conversa aqui pelo WhatsApp. ' +
      'Depois, você receberá indicações de participantes que fazem sentido para o que ' +
      'você busca.\n\n' +
      'Posso começar? Se topar, a próxima mensagem pede sua autorização para usar seus dados.',
    footer: SAIDA,
    /**
     * A porta de entrada, e a de saída ao lado dela.
     *
     * Os mesmos rótulos do lembrete, palavra por palavra. Não é economia de
     * criatividade: o lembrete cobra exatamente este passo, e ver duas chamadas
     * diferentes para a mesma ação faria a pessoa pensar que são coisas
     * distintas.
     *
     * "Quero participar" NÃO é consentimento — é presença. Quem toca aqui ainda
     * vai receber a pergunta da LGPD com as três opções, porque autorizar o
     * tratamento dos dados é escolha específica e informada, e um botão que diz
     * "quero participar" não informa nada sobre isso.
     */
    botoes: [
      ACEITAR_JORNADA,
      RECUSAR_JORNADA,
    ],
  },
  {
    id: 'consentimento',
    nome: '2. Consentimento (LGPD)',
    categoria: 'consentimento',
    quando:
      'Logo depois das boas-vindas. Nada começa sem a resposta desta — nem perguntas, nem análise de perfil.',
    /**
     * Enxugado de 1262 para caber nos 1024 do corpo, e ainda abrindo espaço
     * para a escolha do telefone, que o texto original não tinha.
     *
     * O que foi mantido, um a um, porque é o que a LGPD exige de um
     * consentimento: quem trata (a organização E a MatériaPrimAi), quais dados,
     * para que finalidade, que o áudio é transcrito e analisado, o que é
     * mostrado a outros participantes, que participar é opcional e o que
     * acontece se recusar, a revogação gratuita pelo mesmo canal, os direitos
     * de acesso/correção/exclusão, e o link do aviso completo.
     *
     * O que saiu: repetição entre os blocos e rodeio de redação. Nenhum
     * elemento foi removido — apenas ditos uma vez.
     */
    body:
      'Antes de começarmos, {{nome_participante}}, preciso da sua autorização. 🔒\n\n' +
      '*O que fazemos:* a {{nome_organizacao}} e a MatériaPrimAi usam seu nome, telefone, ' +
      'LinkedIn ou currículo e suas respostas em texto ou áudio (os áudios são transcritos ' +
      'e analisados) para montar seu perfil com inteligência artificial e sugerir conexões ' +
      'no evento, por este WhatsApp.\n\n' +
      '*Quem vê:* as pessoas que indicarmos a você recebem seu nome e um resumo do seu ' +
      'perfil. Seu telefone só é passado quando vocês dois aceitam a indicação.\n\n' +
      'Participar é opcional e sua inscrição no evento vale de qualquer jeito. Você pode ' +
      'retirar a autorização quando quiser, de graça, por aqui, e pedir acesso, correção ou ' +
      'exclusão dos seus dados (LGPD): {{link_privacidade}}\n\n' +
      'Você autoriza?',
    /**
     * Dois botões desde 17/10/2026 — eram três ("com telefone", "sem telefone",
     * "não autorizo").
     *
     * O Gabriel achou a escolha do telefone confusa, e era: ela pedia uma
     * decisão sobre o futuro (o número ir junto em indicações que a pessoa nem
     * viu) no momento em que ela só queria começar. Ficou um regime só, o mais
     * protetor: o número só é passado quando os dois lados aceitam aquela
     * indicação. "Autorizo" cai em `sem_telefone` (ver `leuConsentimento`).
     *
     * O texto foi reorganizado em blocos com título, sem perder nenhum elemento
     * que a LGPD pede: quem trata (organização E MatériaPrimAi), quais dados,
     * finalidade, áudio transcrito e analisado, o que outros participantes veem,
     * participação opcional e o que acontece na recusa, revogação gratuita pelo
     * mesmo canal, acesso/correção/exclusão e o link do aviso completo.
     */
    botoes: [
      { tipo: 'RESPOSTA_RAPIDA', texto: 'Autorizo' },
      { tipo: 'RESPOSTA_RAPIDA', texto: 'Não autorizo' },
    ],
  },
  {
    id: 'lembrete_consentimento',
    nome: 'Lembrete de quem não respondeu',
    categoria: 'lembrete',
    quando:
      'Dias depois, para quem ficou em "Aguardando consentimento". Sem um destes aprovado, quem não responde nunca é cobrado.',
    body:
      'Oi {{nome_participante}}, tudo bem? Mandei uma mensagem sobre a experiência de ' +
      'conexões do {{nome_evento}} e imagino que tenha passado batido.\n\n' +
      'A proposta continua de pé: uma conversa rápida por aqui e eu aponto quem vale a ' +
      'pena você conhecer no evento.\n\n' +
      'Quer começar?',
    botoes: [
      ACEITAR_JORNADA,
      RECUSAR_JORNADA,
    ],
  },
  {
    id: 'conexao_com_telefone',
    nome: '8. Conexão — com telefone',
    categoria: 'recomendacao',
    quando:
      'Uma indicação, para quem escolheu "Autorizo com telefone". Leva o contato direto.',
    /**
     * Só para quem autorizou o telefone no consentimento.
     *
     * A escolha da pessoa é o que decide qual dos dois modelos de conexão sai —
     * é a decisão de 09/09/2026, que substituiu tanto o A6 puro (nunca mandar
     * contato) quanto a etapa 8 pura (sempre mandar). Mandar este para quem
     * escolheu a segunda opção seria divulgar um telefone sem autorização.
     *
     * O texto da especificação começava com "{{nome_participante}}," — a Meta
     * recusa mensagem que começa com variável. Duas palavras antes resolvem.
     */
    body:
      'Boa notícia, {{nome_participante}}! Identificamos uma conexão extraordinária para ' +
      'você no {{nome_evento}}. 🤝\n\n' +
      'Conheça esta pessoa:\n' +
      '👤 {{nome_recomendado}}\n' +
      '📱 {{telefone_recomendado}}\n\n' +
      'Por que essa conexão faz sentido para você?\n' +
      '{{motivo}}\n\n' +
      'É só chamar no WhatsApp e se apresentar como participante do evento. Boa ' +
      'conversa! 😊',
  },
  {
    id: 'conexao_sem_telefone',
    nome: '8. Conexão — pedindo confirmação',
    categoria: 'recomendacao',
    quando:
      'Uma indicação, para quem escolheu "Autorizo sem telefone". O contato só é trocado com os dois lados de acordo.',
    /**
     * A dupla confirmação do A6, agora como um dos dois caminhos em vez de
     * regra única.
     *
     * Nenhum contato no texto — é o que o teste cobra. Quem topa aciona a
     * pergunta ao outro lado; só com os dois de acordo o contato muda de mãos,
     * com consentimento próprio (`purpose = troca_de_contato`).
     */
    body:
      'Boa notícia, {{nome_participante}}! Identificamos uma conexão extraordinária para ' +
      'você no {{nome_evento}}. 🤝\n\n' +
      'É {{nome_recomendado}}, e o motivo é direto — {{motivo}}.\n\n' +
      'Quer que eu apresente vocês dois? Se você topar, faço a mesma pergunta para a ' +
      'outra pessoa, e o contato só é trocado com os dois de acordo.',
    botoes: [
      { tipo: 'RESPOSTA_RAPIDA', texto: 'Sim, pode apresentar' },
      { tipo: 'RESPOSTA_RAPIDA', texto: 'Agora não' },
    ],
  },
  {
    id: 'conexoes_pendentes',
    nome: '8. Ainda faltam conexões',
    categoria: 'recomendacao',
    quando:
      'Ao fim de cada rodada que trouxer indicação nova, enquanto o total for menor que cinco.',
    body:
      'Até aqui, {{nome_participante}}, você já recebeu {{quantidade_enviada}} das suas ' +
      'cinco indicações de conexão. 🤝\n\n' +
      'As restantes chegam conforme outros participantes concluírem a jornada e novas ' +
      'compatibilidades forem identificadas.\n\n' +
      'Enquanto isso, aproveite para conversar com quem já indicamos. As próximas ' +
      'sugestões chegam por aqui até o final do evento. 😊',
  },
  {
    id: 'conexoes_completas',
    nome: '8. As cinco conexões',
    categoria: 'recomendacao',
    quando: 'Ao completar as cinco indicações. Encerra os avisos de pendência.',
    body:
      'Pronto, {{nome_participante}}! Você recebeu suas cinco indicações de conexões ' +
      'extraordinárias no {{nome_evento}}. 🤝\n\n' +
      'Agora é hora de iniciar as conversas e explorar o que vocês podem construir ' +
      'juntos. Que esses encontros tragam boas parcerias e novas oportunidades!',
  },
  {
    id: 'reabertura',
    nome: 'Reabertura de conversa',
    categoria: 'reabertura',
    quando:
      'Quando a conversa parou no meio e passaram 24 horas. Fora dessa janela, só um template aprovado reabre.',
    body:
      'Oi {{nome_participante}}! Nossa conversa sobre o {{nome_evento}} ficou pela ' +
      'metade.\n\n' +
      'Se ainda quiser terminar, é só responder por aqui que eu continuo de onde ' +
      'paramos — não precisa recomeçar nada.',
    botoes: [
      { tipo: 'RESPOSTA_RAPIDA', texto: 'Quero continuar' },
      RECUSAR_JORNADA,
    ],
  },
];
